import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { location as getLocation } from '@app/selectors/routing';
import { getHomeViewRole, isHomeViewRoleReady } from '@app/selectors/home';
import { trackHomeWidgetLoaded } from './analytics';

/**
 * Tracks widget-level HOME_WIDGET_LOADED events on the Home page.
 *
 * - Fires **once** per widget per Home page visit + view role when the
 *   widget completes its initial data load.
 * - Re-fires when the educator/student toggle changes role (new count).
 * - If a premature `count: 0` was recorded and real data arrives later
 *   for the same role (common for student-only users on login), emits a
 *   corrected event with the real count.
 * - Does **not** re-fire on subsequent data refreshes, polling,
 *   pagination, or widget re-renders for the same role once a non-zero
 *   (or settled empty) count is locked.
 * - Uses a WeakMap keyed on the routing location object so tracking
 *   state is automatically garbage-collected when the user navigates
 *   away, and fresh tracking begins on the next Home visit.
 */
const trackedObjectSessions = new WeakMap<object, Map<string, number>>();
const trackedPrimitiveSessions = new Map<
  string | number | boolean | symbol,
  Map<string, number>
>();

const getSessionCounts = (key: unknown): Map<string, number> | undefined => {
  if (typeof key === 'object' && key !== null) {
    return trackedObjectSessions.get(key);
  }
  if (
    typeof key === 'string' ||
    typeof key === 'number' ||
    typeof key === 'boolean' ||
    typeof key === 'symbol'
  ) {
    return trackedPrimitiveSessions.get(key);
  }
  return undefined;
};

const setSessionCounts = (key: unknown, counts: Map<string, number>): void => {
  if (typeof key === 'object' && key !== null) {
    trackedObjectSessions.set(key, counts);
  } else if (
    typeof key === 'string' ||
    typeof key === 'number' ||
    typeof key === 'boolean' ||
    typeof key === 'symbol'
  ) {
    trackedPrimitiveSessions.set(key, counts);
  }
};

const getTrackingKey = (widgetName: string, viewRole: string) =>
  `${widgetName}::${viewRole}`;

const useTrackHomeWidgetLoaded = (
  widgetName: string,
  isLoaded: boolean,
  count: number
) => {
  const routingLocation = useSelector(getLocation);
  const viewRole = useSelector(getHomeViewRole);
  const isViewRoleReady = useSelector(isHomeViewRoleReady);
  const hasFiredRef = useRef(false);
  const lastTrackingKeyRef = useRef<string | null>(null);

  const trackingKey = getTrackingKey(widgetName, viewRole);

  useEffect(() => {
    if (lastTrackingKeyRef.current !== trackingKey) {
      lastTrackingKeyRef.current = trackingKey;
      hasFiredRef.current = false;
    }

    if (!isLoaded || !isViewRoleReady || routingLocation == null) {
      return;
    }

    let sessionCounts = getSessionCounts(routingLocation);
    if (!sessionCounts) {
      sessionCounts = new Map();
      setSessionCounts(routingLocation, sessionCounts);
    }

    const previousCount = sessionCounts.get(trackingKey);

    if (previousCount !== undefined) {
      // Correct a premature zero once real items are available.
      if (previousCount === 0 && count > 0) {
        sessionCounts.set(trackingKey, count);
        hasFiredRef.current = true;
        trackHomeWidgetLoaded(widgetName, count, viewRole);
        return;
      }

      hasFiredRef.current = true;
      return;
    }

    if (hasFiredRef.current) {
      return;
    }

    hasFiredRef.current = true;
    sessionCounts.set(trackingKey, count);
    trackHomeWidgetLoaded(widgetName, count, viewRole);
  }, [
    isLoaded,
    isViewRoleReady,
    widgetName,
    count,
    routingLocation,
    trackingKey,
    viewRole,
  ]);
};

export default useTrackHomeWidgetLoaded;
